import { useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { apiRequest } from "@/lib/queryClient";

/**
 * Shared document-upload flow for a generic-section row, used by BOTH the category
 * page (ExpandableSectionLayout) and the guided wizard (GuidedStagePage). It owns the
 * network steps only — resolve/create the row's first-field entry, attach the file
 * (server→R2), then run Fill-from-Document extraction — and returns structured results.
 * Each caller wires its own UI (document list, pending-extraction review modal) from
 * the returned data. Single source of truth for the upload+extract round-trip.
 *
 * `categoryId` is optional: the category page passes it (so brand-new rows can be
 * created); the wizard omits it (its rows are seeded/static, so `upsertGenericEntry`
 * updates in place and returns the id without needing the category).
 */

export interface RowUploadField {
  id: string;
  sectionId: string;
}

export interface RowUploadParams {
  clientId: string;
  /** The section being uploaded into. Only id + fields[] are required. */
  section: { id: string; fields: RowUploadField[] };
  /** Row to attach to. `null` for single-entry (card) sections. */
  rowId: string | null;
  file: File;
  /** Optional — passed by the category page so a not-yet-existing row can be created. */
  categoryId?: string;
  /** Caller-resolved existing first-field entry, if known (avoids a redundant upsert). */
  existingEntry?: { id: string } | null;
}

export interface RowUploadDocument {
  id: string;
  filename: string;
  fileSize?: number;
  mimeType?: string;
  uploadedAt?: string;
}

export interface RowUploadResult {
  entry: { id: string };
  document: RowUploadDocument;
  /**
   * The async extraction job queued for this upload (session 90), or null if the
   * extract call failed to enqueue. The caller watches `job.jobId` for completion;
   * the document is attached regardless.
   */
  job: { jobId: string } | null;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function useGenericRowUpload() {
  // D-170.6: the three requests below are ONE logical operation, so they convert to ONE
  // mutation rather than three. Class A on two counts — step 2 INSERTs a generic_documents
  // row (and a fresh R2 object), step 3 enqueues a billable Gemini extraction. Step 1 is the
  // usual (client, field, row) upsert and is class C on its own.
  //
  // The lock lives here rather than at the two call sites because both of them wrap this in a
  // per-file-drop handler that takes exactly ONE file (no `multiple` input feeds either), so a
  // hook-level lock serialises duplicates without swallowing a legitimate second upload.
  const uploadFlight = useSingleFlight();
  const uploadMutation = useMutation({
    mutationFn: async (p: RowUploadParams): Promise<RowUploadResult> => {
    const firstField = p.section.fields[0];
    if (!firstField) throw new Error("Section has no fields to attach a document to");

    // 1. Resolve (or create) the row's first-field entry — documents bind to it.
    //    upsertGenericEntry matches (clientId, fieldId, rowId): existing rows UPDATE
    //    in place (no categoryId needed), so passing no value/categoryId is safe.
    let entry = p.existingEntry ?? null;
    if (!entry) {
      const body: Record<string, unknown> = {
        sectionId: firstField.sectionId,
        fieldId: firstField.id,
        rowId: p.rowId,
      };
      if (p.categoryId) body.categoryId = p.categoryId;
      const createRes = await apiRequest("POST", `/api/clients/${p.clientId}/generic-entries`, body);
      if (createRes.ok) entry = await createRes.json();
    }
    if (!entry) throw new Error("Could not resolve the row entry");

    // Read the file once — reused for attach + extract.
    const base64 = await fileToBase64(p.file);

    // 2. ATTACH — server-side upload (server→R2): no browser CORS, checksum-safe.
    const docRes = await apiRequest("POST", `/api/generic-entries/${entry.id}/documents/upload`, {
      data: base64, filename: p.file.name, contentType: p.file.type,
    });
    const document = (await docRes.json()) as RowUploadDocument;

    // 3. EXTRACT — enqueue the async extraction job (session 90). Returns a jobId
    //    immediately; the worker runs Gemini and notifies via SSE / job polling.
    let job: { jobId: string } | null = null;
    try {
      const exRes = await apiRequest("POST", `/api/clients/${p.clientId}/sections/${p.section.id}/extract-document`, {
        data: base64, contentType: p.file.type, filename: p.file.name, rowId: p.rowId,
        // s96: link the extraction to the just-attached document so the worker can
        // persist the AI summary onto that document record.
        documentId: document.id,
      });
      if (exRes.ok) {
        const body = await exRes.json();
        if (body?.jobId) job = { jobId: body.jobId };
      }
    } catch {
      // Enqueue is best-effort; the attach already succeeded.
      job = null;
    }

      return { entry, document, job };
    },
  });

  /**
   * Resolves `undefined` when the lock rejected the call as a duplicate. Both callers
   * destructure the result, so they must not be handed a half-finished upload — they check
   * for the skip before reading it.
   */
  const uploadRowDocument = useCallback(
    (p: RowUploadParams) => uploadFlight.run(() => uploadMutation.mutateAsync(p)),
    [uploadFlight, uploadMutation],
  );

  return { uploadRowDocument };
}
